/**
 * CompetitorMatrix — Full competitive comparison table.
 *
 * Crown & Rose vs 9 competitors. Framework-driven, one subscription, all tools.
 * Positioned as: why Crown & Rose authors need no other tool.
 */
import React, { useState } from 'react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';

// ── Status helpers ──────────────────────────────────────────────────────────
const S = {
  full:    { symbol: '✓', label: 'Yes',     bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200' },
  partial: { symbol: '△', label: 'Partial', bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200'  },
  none:    { symbol: '✗', label: 'No',      bg: 'bg-red-50',      text: 'text-red-500',     border: 'border-red-100'    },
};

function Badge({ status }) {
  const s = S[status] || S.none;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${s.bg} ${s.text} ${s.border}`}
    >
      {s.symbol} {s.label}
    </span>
  );
}

// ── Matrix data ─────────────────────────────────────────────────────────────
const MATRIX = [
  {
    feature: 'Platform Type',
    cr:   { status: 'full',    text: 'End-to-end novel production — write, edit, audit, publish. One subscription, all tools.' },
    nc:   { status: 'partial', text: 'Novel planning + BYOK AI assistance' },
    sw:   { status: 'partial', text: 'Creative AI prose generation and inspiration' },
    nai:  { status: 'partial', text: 'Unrestricted fiction continuation' },
    gr:   { status: 'none',    text: 'Grammar and clarity editor only' },
    scr:  { status: 'partial', text: 'Desktop manuscript organiser — no AI' },
    att:  { status: 'partial', text: 'Formatting + writing tool — no AI' },
    vel:  { status: 'none',    text: 'Book formatting and export only' },
    pwa:  { status: 'none',    text: 'Grammar & style checker — no story tools' },
    jas:  { status: 'none',    text: 'Marketing copy AI — not fiction-native' },
  },
  {
    feature: 'Framework-Driven Workflows',
    highlight: true,
    cr:   { status: 'full',    text: 'Plot Blueprint, Character Bible, KDP Framework, Edit Walker — authors fill guided forms, never craft AI prompts. Zero AI expertise required.' },
    nc:   { status: 'partial', text: 'Structured Codex — still requires manual BYOK prompt setup' },
    sw:   { status: 'partial', text: 'Partial guidance — users configure AI directly for most tasks' },
    nai:  { status: 'none',    text: 'Raw continuation only — no guided framework' },
    gr:   { status: 'none',    text: '—' },
    scr:  { status: 'none',    text: '—' },
    att:  { status: 'none',    text: '—' },
    vel:  { status: 'none',    text: '—' },
    pwa:  { status: 'none',    text: '—' },
    jas:  { status: 'none',    text: 'Prompt-based — no structured framework layer' },
  },
  {
    feature: 'Ease of Use',
    highlight: true,
    cr:   { status: 'full',    text: 'Easy — guided frameworks insulate authors from all AI complexity. No prompts, no settings, no learning curve. Writers fill forms; the platform does the rest.' },
    nc:   { status: 'partial', text: 'Moderate — Codex setup and BYOK key configuration require technical familiarity' },
    sw:   { status: 'partial', text: 'Moderate — authors configure AI modes, models, and context windows directly' },
    nai:  { status: 'partial', text: 'Moderate — Lorebook setup and model context management require learning' },
    gr:   { status: 'full',    text: 'Easy for grammar checking; limited to proofreading scope' },
    scr:  { status: 'partial', text: 'Moderate — steep compile and binder setup; no AI to guide authors' },
    att:  { status: 'full',    text: 'Easy for formatting; no AI features' },
    vel:  { status: 'full',    text: 'Easy for formatting only; requires a separate writing tool' },
    pwa:  { status: 'full',    text: 'Easy for grammar review; no story or production tools' },
    jas:  { status: 'partial', text: 'Moderate — prompt engineering and template configuration required for quality output' },
  },
  {
    feature: 'KDP Publishing Framework',
    highlight: true,
    cr:   { status: 'full',    text: 'Guided KDP workflow: metadata, 7 keywords, content-policy compliance, front/back matter, publish-ready checklist, EPUB export — built into every project.' },
    nc:   { status: 'none',    text: '—' },
    sw:   { status: 'none',    text: '—' },
    nai:  { status: 'none',    text: '—' },
    gr:   { status: 'none',    text: '—' },
    scr:  { status: 'none',    text: 'Compile to EPUB — no KDP compliance guidance' },
    att:  { status: 'partial', text: 'KDP-compatible export — no compliance workflow' },
    vel:  { status: 'partial', text: 'KDP-ready export — no metadata or compliance guidance' },
    pwa:  { status: 'none',    text: '—' },
    jas:  { status: 'none',    text: '—' },
  },
  {
    feature: 'AI Drafting',
    cr:   { status: 'full', text: 'Claude, GPT, Gemini — per project, per chapter' },
    nc:   { status: 'full', text: 'BYOK, scene-based generation' },
    sw:   { status: 'full', text: 'Multi-model (Claude / GPT / custom)' },
    nai:  { status: 'full', text: 'Proprietary model, continuation-focused' },
    gr:   { status: 'none', text: 'Rewriting only — no drafting' },
    scr:  { status: 'none', text: 'No AI drafting' },
    att:  { status: 'none', text: 'No AI' },
    vel:  { status: 'none', text: 'No AI' },
    pwa:  { status: 'none', text: 'No AI drafting' },
    jas:  { status: 'partial', text: 'Marketing copy AI — not long-form fiction' },
  },
  {
    feature: 'Bulk Novel Generation',
    highlight: true,
    cr:   { status: 'full', text: 'Entire book — all chapters — in one automated job' },
    nc:   { status: 'none', text: '—' },
    sw:   { status: 'none', text: '—' },
    nai:  { status: 'none', text: '—' },
    gr:   { status: 'none', text: '—' },
    scr:  { status: 'none', text: '—' },
    att:  { status: 'none', text: '—' },
    vel:  { status: 'none', text: '—' },
    pwa:  { status: 'none', text: '—' },
    jas:  { status: 'none', text: '—' },
  },
  {
    feature: 'Plot Framework',
    highlight: true,
    cr:   { status: 'full',    text: 'Plot Blueprint: logline, antagonist, themes, act turning points, 85% Sales Readiness Score' },
    nc:   { status: 'full',    text: 'Codex, series management' },
    sw:   { status: 'partial', text: 'Story Bible — no structural scoring' },
    nai:  { status: 'partial', text: 'Lorebook — no macro-plot tools' },
    gr:   { status: 'none',    text: '—' },
    scr:  { status: 'partial', text: 'Binder organisation — no plot scoring' },
    att:  { status: 'none',    text: '—' },
    vel:  { status: 'none',    text: '—' },
    pwa:  { status: 'none',    text: '—' },
    jas:  { status: 'none',    text: '—' },
  },
  {
    feature: 'Arc Analysis',
    highlight: true,
    cr:   { status: 'full', text: 'Deterministic per-character scoring + multi-dimension arc breakdown' },
    nc:   { status: 'none', text: '—' },
    sw:   { status: 'none', text: '—' },
    nai:  { status: 'none', text: '—' },
    gr:   { status: 'none', text: '—' },
    scr:  { status: 'none', text: '—' },
    att:  { status: 'none', text: '—' },
    vel:  { status: 'none', text: '—' },
    pwa:  { status: 'none', text: '—' },
    jas:  { status: 'none', text: '—' },
  },
  {
    feature: 'Continuity Audit',
    highlight: true,
    cr:   { status: 'full',    text: 'Full walk + drift correction + auto-walks + history trend chart' },
    nc:   { status: 'partial', text: 'Basic review tools' },
    sw:   { status: 'partial', text: 'Context memory — not a structured audit' },
    nai:  { status: 'none',    text: '—' },
    gr:   { status: 'none',    text: '—' },
    scr:  { status: 'none',    text: '—' },
    att:  { status: 'none',    text: '—' },
    vel:  { status: 'none',    text: '—' },
    pwa:  { status: 'none',    text: '—' },
    jas:  { status: 'none',    text: '—' },
  },
  {
    feature: 'Developmental Editing',
    highlight: true,
    cr:   { status: 'full', text: 'Edit Walker — Quick Pass (style & prose) + Full Structural depth mode (Opus analysis). Accept / Reject / Compare per chapter.' },
    nc:   { status: 'none', text: '—' },
    sw:   { status: 'none', text: 'Margin notes only' },
    nai:  { status: 'none', text: '—' },
    gr:   { status: 'none', text: 'Surface polish only' },
    scr:  { status: 'none', text: '—' },
    att:  { status: 'none', text: '—' },
    vel:  { status: 'none', text: '—' },
    pwa:  { status: 'partial', text: 'Editing suggestions only' },
    jas:  { status: 'none', text: '—' },
  },
  {
    feature: 'Manuscript Polish',
    highlight: true,
    cr:   { status: 'full',    text: 'Whole-manuscript polish with per-chapter Accept / Skip review' },
    nc:   { status: 'none',    text: '—' },
    sw:   { status: 'partial', text: 'Single-scene rewrite' },
    nai:  { status: 'none',    text: '—' },
    gr:   { status: 'partial', text: 'Sentence-level rewrites only' },
    scr:  { status: 'none',    text: '—' },
    att:  { status: 'none',    text: '—' },
    vel:  { status: 'none',    text: '—' },
    pwa:  { status: 'partial', text: 'Style-level suggestions' },
    jas:  { status: 'none',    text: '—' },
  },
  {
    feature: 'Voice Consistency',
    highlight: true,
    cr:   { status: 'full',    text: 'Voice Coach + series-level voice memory across chapters' },
    nc:   { status: 'none',    text: '—' },
    sw:   { status: 'none',    text: '—' },
    nai:  { status: 'none',    text: '—' },
    gr:   { status: 'partial', text: 'Tone suggestions — not fiction-specific' },
    scr:  { status: 'none',    text: '—' },
    att:  { status: 'none',    text: '—' },
    vel:  { status: 'none',    text: '—' },
    pwa:  { status: 'partial', text: 'Style reports — not per-character voice' },
    jas:  { status: 'none',    text: '—' },
  },
  {
    feature: 'KDP Compliance',
    highlight: true,
    cr:   { status: 'full',    text: 'Full KDP framework: metadata, 7 keywords, content-policy, front/back matter, checklist, EPUB export' },
    nc:   { status: 'none',    text: '—' },
    sw:   { status: 'none',    text: '—' },
    nai:  { status: 'none',    text: '—' },
    gr:   { status: 'none',    text: '—' },
    scr:  { status: 'none',    text: 'Compile to EPUB — no compliance checking' },
    att:  { status: 'partial', text: 'KDP-compatible export — no compliance workflow' },
    vel:  { status: 'partial', text: 'Beautiful formatting — no compliance or metadata guidance' },
    pwa:  { status: 'none',    text: '—' },
    jas:  { status: 'none',    text: '—' },
  },
  {
    feature: 'Manuscript Readiness Check',
    highlight: true,
    cr:   { status: 'full', text: 'Weekly completeness digest — emails authors about missing synopsis, cover, keywords, word count with direct fix links' },
    nc:   { status: 'none', text: '—' },
    sw:   { status: 'none', text: '—' },
    nai:  { status: 'none', text: '—' },
    gr:   { status: 'none', text: '—' },
    scr:  { status: 'none', text: '—' },
    att:  { status: 'none', text: '—' },
    vel:  { status: 'none', text: '—' },
    pwa:  { status: 'none', text: '—' },
    jas:  { status: 'none', text: '—' },
  },
  {
    feature: 'Book Formatting & Export',
    cr:   { status: 'full',    text: 'DOCX, PDF (print-trim), KDP-ready EPUB, ISBN Manager' },
    nc:   { status: 'partial', text: 'DOCX export only' },
    sw:   { status: 'partial', text: 'Copy-paste or plain export' },
    nai:  { status: 'none',    text: '—' },
    gr:   { status: 'none',    text: '—' },
    scr:  { status: 'full',    text: 'Best-in-class compile — ePub, DOCX, PDF' },
    att:  { status: 'full',    text: 'Print-ready PDF + ePub formatting' },
    vel:  { status: 'full',    text: 'Gold standard formatting — requires separate writing tool' },
    pwa:  { status: 'none',    text: '—' },
    jas:  { status: 'none',    text: '—' },
  },
  {
    feature: 'Marketing Tools',
    highlight: true,
    cr:   { status: 'full', text: 'Amazon blurb, ARC pitch, tweet thread, social calendar, reviewer CRM' },
    nc:   { status: 'none', text: '—' },
    sw:   { status: 'none', text: '—' },
    nai:  { status: 'none', text: '—' },
    gr:   { status: 'none', text: '—' },
    scr:  { status: 'none', text: '—' },
    att:  { status: 'none', text: '—' },
    vel:  { status: 'none', text: '—' },
    pwa:  { status: 'none', text: '—' },
    jas:  { status: 'partial', text: 'Marketing copy — not book-specific CRM' },
  },
  {
    feature: 'Prompt Caching',
    highlight: true,
    cr:   { status: 'full', text: 'Across Edit Walker, Continuity Review & Retroactive Review — lowers LLM cost ~37%' },
    nc:   { status: 'none', text: '—' },
    sw:   { status: 'none', text: '—' },
    nai:  { status: 'none', text: '—' },
    gr:   { status: 'none', text: '—' },
    scr:  { status: 'none', text: '—' },
    att:  { status: 'none', text: '—' },
    vel:  { status: 'none', text: '—' },
    pwa:  { status: 'none', text: '—' },
    jas:  { status: 'none', text: '—' },
  },
  {
    feature: 'Starting Paid Price',
    cr:   { status: 'full',    text: '$39/mo Author — every tool included, no add-ons needed' },
    nc:   { status: 'full',    text: '$4–$8/mo (no AI included)' },
    sw:   { status: 'full',    text: '$10/mo (annual)' },
    nai:  { status: 'full',    text: '$10/mo (Tablet)' },
    gr:   { status: 'full',    text: '$12/mo (Pro annual)' },
    scr:  { status: 'full',    text: '$59 one-time (no AI, no cloud)' },
    att:  { status: 'full',    text: '$147 one-time (no AI)' },
    vel:  { status: 'full',    text: '$249.99 one-time + still need a writing tool' },
    pwa:  { status: 'full',    text: '$10/mo (Premium annual)' },
    jas:  { status: 'full',    text: '$39/mo (Creator) — not fiction-native' },
  },
];

const COLS = [
  { key: 'cr',  label: 'Crown \u0026 Rose',   isCr: true  },
  { key: 'nc',  label: 'Novelcrafter',   isCr: false },
  { key: 'sw',  label: 'Sudowrite',      isCr: false },
  { key: 'nai', label: 'NovelAI',        isCr: false },
  { key: 'gr',  label: 'Grammarly',      isCr: false },
  { key: 'scr', label: 'Scrivener',      isCr: false },
  { key: 'att', label: 'Atticus',        isCr: false },
  { key: 'vel', label: 'Vellum',         isCr: false },
  { key: 'pwa', label: 'ProWritingAid',  isCr: false },
  { key: 'jas', label: 'Jasper',         isCr: false },
];


export default function CompetitorMatrix() {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? MATRIX : MATRIX.slice(0, 9);

  return (
    <TooltipProvider delayDuration={200}>
    <section data-testid="competitor-matrix" className="space-y-4">
      {/* Section header */}
      <div className="space-y-1">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-rose-800 border border-rose-200">
          Complete Capability Overview
        </div>
        <h2 className="text-2xl font-bold tracking-tight">
          One Subscription. Every Tool. Nothing to Learn About AI.
        </h2>
        <p className="text-[13px] text-muted-foreground">
          Crown &amp; Rose authors write, edit, audit, polish, format and publish — all inside one platform.
          Guided frameworks handle every step: you never need to craft an AI prompt or understand the technology
          behind it. This is why Crown &amp; Rose authors stop paying for Scrivener, Vellum, Grammarly, and every
          other tool they used to juggle.
        </p>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-[12px]">
        <span className="font-semibold text-muted-foreground">Key:</span>
        {Object.entries(S).map(([k, v]) => (
          <span key={k} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-semibold ${v.bg} ${v.text} ${v.border}`}>
            {v.symbol} {v.label}
          </span>
        ))}
        <span className="ml-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-amber-700 font-semibold">★ Crown &amp; Rose Only</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-border shadow-sm">
        <table className="w-full min-w-[960px] border-collapse text-[12.5px]">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-slate-900 text-left px-4 py-3.5 text-[11px] font-bold uppercase tracking-widest text-slate-300 w-[180px]">
                Feature
              </th>
              {COLS.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3.5 text-left text-[11.5px] font-bold uppercase tracking-wide ${
                    col.isCr
                      ? 'bg-rose-900 text-rose-100 border-x-2 border-x-rose-700'
                      : 'bg-slate-800 text-slate-300'
                  }`}
                >
                  {col.label}
                  {col.isCr && (
                    <span className="ml-1.5 rounded-full bg-rose-700/60 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-rose-200">
                      This platform
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => (
              <tr
                key={row.feature}
                className={`border-t border-border group ${
                  i % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                } hover:bg-rose-50/30 transition-colors`}
              >
                <td className="sticky left-0 z-10 bg-inherit px-4 py-3 font-semibold text-foreground w-[180px] border-r border-border">
                  <span className="flex items-center gap-1.5">
                    {row.feature}
                    {row.highlight && (
                      <span className="shrink-0 rounded-full bg-rose-100 border border-rose-200 px-1.5 py-0.5 text-[9px] font-bold text-rose-700 uppercase tracking-wide">
                        Only here
                      </span>
                    )}
                  </span>
                </td>

                {COLS.map((col) => {
                  const cell = row[col.key];
                  if (!cell) return <td key={col.key} className="px-4 py-3 text-muted-foreground text-center">—</td>;
                  const hasDetail = cell.text && cell.text !== '—';
                  return (
                    <td
                      key={col.key}
                      className={`px-4 py-3 align-top ${
                        col.isCr ? 'bg-rose-50/60 border-x-2 border-x-rose-100' : ''
                      }`}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="inline-flex flex-col items-start gap-1 cursor-default">
                            <Badge status={cell.status} />
                            {hasDetail && (
                              <p className={`text-[13px] leading-snug ${
                                col.isCr ? 'text-slate-700 font-medium' : 'text-muted-foreground'
                              }`}>
                                {cell.text}
                              </p>
                            )}
                          </div>
                        </TooltipTrigger>
                        {hasDetail && (
                          <TooltipContent
                            side="top"
                            className="max-w-[280px] text-[12.5px] leading-snug"
                          >
                            <p className="font-semibold mb-0.5">{col.label}</p>
                            <p>{cell.text}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {MATRIX.length > 9 && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="rounded-full border border-border bg-background px-5 py-1.5 text-[12.5px] font-medium text-muted-foreground transition hover:border-primary hover:text-primary"
            data-testid="matrix-show-more"
          >
            {showAll ? '↑ Show fewer rows' : `↓ Show all ${MATRIX.length} capability rows`}
          </button>
        </div>
      )}

      {/* Crown & Rose exclusives call-out */}
      <div className="rounded-2xl border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-white p-5 shadow-sm">
        <h3 className="text-[14px] font-bold text-rose-900 mb-1">
          Built exclusively inside Crown &amp; Rose — no other tool needed
        </h3>
        <p className="text-[12px] text-rose-800/70 mb-3">
          These capabilities exist nowhere else. Every serious author feature you might seek in a separate tool
          is already inside Crown &amp; Rose.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {[
            { title: 'Direct-Entry Plot Framework', desc: 'Logline, antagonist, themes and act turning points locked in before a word is drafted.' },
            { title: 'Bulk Novel Generation + Commit & Rebuild', desc: 'Draft every chapter in one automated job, then rebuild the whole manuscript with a single click.' },
            { title: 'Arc Visualizer — Deterministic Scoring', desc: 'Name-mention frequency across all chapters with multi-dimensional per-character breakdown.' },
            { title: 'Edit Walker Dual-Pass Architecture', desc: 'Opus analysis pass + prose pass walks every chapter — accept, reject, or compare per chapter.' },
            { title: 'Continuity Auto-Walk + Drift Correction', desc: 'Nightly walks, drift history trend charts, one-click bulk rework for all flagged chapters.' },
            { title: 'Whole-Manuscript Polish Review', desc: 'AI polishes every chapter then gives you per-chapter Accept / Skip controls before any change lands.' },
            { title: 'KDP Compliance Checker', desc: 'Metadata, content-policy flags, front/back matter templates and a publish-ready checklist in one dialog.' },
            { title: 'Marketing Suite + ARC CRM', desc: 'Amazon blurb, ARC pitch email, tweet thread, social scheduler, and reviewer pipeline — all in one dialog.' },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-2.5 rounded-xl bg-white border border-rose-100 px-3.5 py-2.5 shadow-sm">
              <span className="mt-0.5 shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white text-[10px] font-bold">✓</span>
              <div>
                <p className="text-[12px] font-semibold text-slate-800">{item.title}</p>
                <p className="text-[11.5px] text-muted-foreground leading-snug mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
    </TooltipProvider>
  );
}

